/**
 * @author Housefolios
 * @createTime 2025-01-27 10:00:00
 * @modifiedBy AI Assistant
 * @modifiedTime 2025-01-27 10:00:00
 * @description Common card components for strategy pages
 */

import React from 'react'
import {
  strategyBorderMutedSx,
  strategyCardTitleSx,
  strategyCenterTextSx,
  strategyDarkTextSx,
  strategyMutedValueSx,
  strategyMarginBottom25Sx,
  strategyMarginTop25Sx,
  strategyMetricGrayBgSx,
  strategyMetricRowFlexSx,
  strategyMutedPadXSx,
  strategyNavyBgSx,
  calcDialogPaperSx,
  strategyPadBottom20Sx,
  strategyTextMuted802Sx,
  strategyWhiteTextSx,
  clampLinearProgressValue,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CountUp from '@/utils/SafeCountUp'
import { map, isUndefined, round } from '@/utils/lodash'

import {
  Avatar,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItemButton,
  LinearProgress,
  Tooltip,
  Typography,
  CardHeader,
  Dialog,
  CardActions,
  Box,
} from '@mui/material'

import GaugeChart from '@/utils/SafeGaugeChart'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'

import { getStrategyName } from '@/utils/stratagies'
import { useColorMode } from '@/theme/ColorModeProvider'
import { strategyMetricProgressSx } from '@/theme/skinSx'

/**
 * Strategy card component
 * @param {Object} props - Component props
 * @param {string} props.strategyName - Name of the strategy
 * @param {Object} props.activeOrg - Active organization
 * @param {number} props.percentage - Strategy percentage
 * @param {Function} props.onStrategyTourClick - Function to handle strategy tour click
 * @param {boolean} props.shared - Whether this is a shared view
 * @param {Object} props.property - Property object
 * @param {boolean} props.canDrag - Whether cards can be dragged
 * @param {React.ReactNode} props.contactAgent - Contact agent component
 * @param {React.ReactNode} [props.children] - Optional member-specific footer content
 * @returns {React.ReactElement} Strategy card
 */
export const StrategyCard = ({
  strategyName,
  activeOrg,
  percentage,
  onStrategyTourClick,
  shared = false,
  property,
  canDrag,
  contactAgent,
  children,
}) => {
  const { resolvedMode } = useColorMode()
  const needleColor = resolvedMode === 'dark' ? '#e0e0e0' : '#464A4F'

  return (
    <Card variant="outlined"
     
      data-tour="strategy-step-1"
      sx={{ width: '100%' }}
    >
      <Box sx={{
        display: "flex",
        alignItems: "center",
        py: "0.75rem",
        px: "1.25rem",
        mb: 0,
        backgroundColor: "#fff",
        padding: "0.75rem 1.25rem",
        marginBottom: 0,
        borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
        "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
        "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
      }}>
        <Box sx={{
          flexGrow: 1,
          minWidth: 0,
          "& > b": { display: "block" },
          "& > small": {
            textTransform: "uppercase",
            display: "block",
            opacity: 0.3,
            fontWeight: "bold",
          },
        }}>
          <Box
            component="small"
            sx={{
              display: 'block',
              textTransform: 'uppercase',
              mt: '0.25rem',
            }}
          >
            Strategy
          </Box>
          <b>{getStrategyName(strategyName, activeOrg?.member)}</b>
        </Box>
        <div>
          <Tooltip title="Quick glance determination seen on the main page">
            <Button
              onClick={onStrategyTourClick}
             
              sx={{
                backgroundColor: "rgba(54, 140, 255, 0.15)",
                color: "#368cff",
                borderColor: "transparent",
                "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                height: "30px !important",
                width: "30px !important",
                lineHeight: "30px !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0,
                border: 0,
                textAlign: "left",
              }}
            >
              <FontAwesomeIcon icon={['fal', 'info-circle']} />
            </Button>
          </Tooltip>
        </div>
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', px: '1.5rem', pt: '1.5rem', pb: '1rem', }} />
        <Box sx={[strategyPadBottom20Sx, { mx: 'auto' }]}>
          <GaugeChart
            id="chartsGauges3B"
            nrOfLevels={6}
            colors={['#f83245', '#f4772e', '#1bc943']}
            arcWidth={0.4}
            hideText
            percent={percentage}
            needleColor={needleColor}
            needleBaseColor={needleColor}
          />
        </Box>
        <Box
          component="p" sx={[strategyMutedPadXSx, { mb: '1rem', textAlign: 'center' }]}>
          According to your inputs, a {strategyName} strategy meets{' '}
          {percentage < 0.33 && (
            <Box component="b" sx={{ color: '#f83245' }}>
              {round(percentage * 100)}%
            </Box>
          )}
          {percentage > 0.33 && percentage < 0.66 && (
            <Box component="b" sx={{ color: '#f4772e' }}>
              {round(percentage * 100)}%
            </Box>
          )}
          {percentage > 0.66 && (
            <Box component="b" sx={{ color: '#1bc943' }}>
              {round(percentage * 100)}%
            </Box>
          )}{' '}
          of your targets
        </Box>
        {!shared && contactAgent}
        {children}
      </Box>
    </Card>
  )
}

/**
 * Targets card component
 * @param {Object} props - Component props
 * @param {Object} props.strategySummary - Strategy summary data
 * @param {Object} props.currencyFormat - Currency formatter
 * @param {Object} props.percentageFormat - Percentage formatter
 * @param {string} props.modal1 - Current modal state
 * @param {Function} props.toggle1 - Function to handle modal toggle
 * @param {Function} props.toggle1Close - Function to close modal
 * @param {Function} props.renderInputFields - Function to render input fields
 * @param {Object} props.assumptionsInput - Assumptions input data
 * @param {number} props.maxOfferPrice - Maximum offer price based on targets
 * @param {Function} props.toggleMaxOffer - Function to toggle max offer dialog
 * @param {boolean} props.maxOfferDescription - Whether max offer dialog is open
 * @param {Object} props.maxOfferCalculation - The element to display max offer relevant values
 * @param {Function} [props.onTargetsTourClick] - Optional targets tour opener
 * @param {boolean} [props.showMaxOffer=true] - Whether to show the max offer summary/dialog
 * @returns {React.ReactElement} Targets card
 */
export const TargetsCard = ({
  strategySummary,
  currencyFormat,
  percentageFormat,
  modal1,
  toggle1,
  toggle1Close,
  renderInputFields,
  assumptionsInput,
  maxOfferPrice,
  toggleMaxOffer,
  maxOfferDescription,
  maxOfferCalculation,
  onTargetsTourClick,
  showMaxOffer = true,
}) => {
  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        py: "0.75rem",
        px: "1.25rem",
        mb: 0,
        backgroundColor: "#fff",
        padding: "0.75rem 1.25rem",
        marginBottom: 0,
        borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
        "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
        "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
      }}>
        <Box sx={{
          flexGrow: 1,
          minWidth: 0,
          "& > b": { display: "block" },
          "& > small": {
            textTransform: "uppercase",
            display: "block",
            opacity: 0.3,
            fontWeight: "bold",
          },
        }}>
          <Box
            component="small"
            sx={{
              display: 'block',
              textTransform: 'uppercase',
              mt: '0.25rem',
            }}
          >
            Does it meet your
          </Box>
          <b>Targets</b>
        </Box>
        {onTargetsTourClick && (
          <div>
            <Tooltip title="Does this property meet your specified targets?">
              <Button
                onClick={onTargetsTourClick}
               
                sx={{
                  backgroundColor: "rgba(54, 140, 255, 0.15)",
                  color: "#368cff",
                  borderColor: "transparent",
                  "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                  "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                  height: "30px !important",
                  width: "30px !important",
                  lineHeight: "30px !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 0,
                  border: 0,
                  textAlign: "left",
                }}
              >
                <FontAwesomeIcon icon={['fal', 'info-circle']} />
              </Button>
            </Tooltip>
          </div>
        )}
      </Box>
      <Divider />
      {showMaxOffer && (
        <>
          <Grid
            container
            direction="column"
            onClick={toggleMaxOffer}
            data-tour="targets-max-offer"
           
            sx={{
              transition: "all 0.2s ease-in-out !important",
              transform: "scale(1) !important",
              boxShadow: "none",
              position: "relative",
              zIndex: "0",
              "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
              display: "flex",
              backgroundColor: "#fff",
              py: "1rem",
              alignItems: "center",
              justifyContent: "space-around",
              "html[data-color-mode=\"dark\"] &": {
                backgroundColor: "#2a2a2a !important",
                color: "rgba(255, 255, 255, 0.87)",
                "& .MuiTypography-root": { color: "rgba(255, 255, 255, 0.87)" },
              },
            }}
          >
            <Grid data-hf-grid="keep">
              <Typography variant="h3">
                {maxOfferPrice > 0
                  ? currencyFormat.format(maxOfferPrice)
                  : '-'}
              </Typography>
            </Grid>
            <Grid
              data-hf-grid="keep"
              sx={strategyCenterTextSx}
            >
              <Typography sx={strategyMutedValueSx}>
                Max offer based on targets
              </Typography>
            </Grid>
          </Grid>
          <Dialog
            open={maxOfferDescription}
            onClose={toggleMaxOffer}
            maxWidth="lg"
            scroll="body"
            slotProps={{ paper: { sx: calcDialogPaperSx } }}
          >
            <Grid container spacing={0} direction="row">
              <Grid size={{ sm: 5 }}>
                <Box sx={[{ height: '100%' }, {
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  "& > .divider": { zIndex: 7, width: "100%" },
                  "& .hero-footer": { zIndex: 7 },
                  "& .header-nav-wrapper": { zIndex: 27 },
                  "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                  borderTopLeftRadius: "inherit",
                  borderTopRightRadius: "inherit",
                  alignItems: "center",
                  ".MuiCard-root > &": {
                    top: -1,
                    marginLeft: -1,
                    marginRight: -1,
                    marginBottom: -2,
                    zIndex: 6,
                  },
                  backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
                }, {
                  "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                }]}>
                  <Box
                    sx={{
                      flexGrow: 1,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={[strategyNavyBgSx, { borderRadius: '0.65rem' }, {
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      width: "100%",
                      zIndex: 5,
                      opacity: "0.7 !important",
                    }, {
                      "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                    }]}/>
                    <Box sx={[{ justifyContent: 'center', textAlign: 'center', p: '3rem', '@media (min-width: 1440px)': { justifyContent: 'flex-start', textAlign: 'left' }, }, { position: "relative", zIndex: "7", width: "100%" }]}>
                      <Box sx={{ color: '#fff', mt: '1rem' }}>
                        <Typography sx={strategyCardTitleSx}>
                          Relevant Data
                        </Typography>
                      </Box>
                      {maxOfferCalculation}
                      <Box sx={{ color: '#fff', mt: '1rem' }}>
                        <Typography>
                          * Calculation may be a few dollars off due
                          to rounding
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ sm: 7 }}>
                <Box sx={[{ height: '100%' }, {
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  "& > .divider": { zIndex: 7, width: "100%" },
                  "& .hero-footer": { zIndex: 7 },
                  "& .header-nav-wrapper": { zIndex: 27 },
                  "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                }, {
                  "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                }]}>
                  <Card sx={{ flexGrow: 1, width: '100%' }}>
                    <CardHeader title="Max Offer" />
                    <Divider />
                    <CardContent>
                      <Grid
                        container
                        direction="row"
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Grid size={12} sx={strategyMarginBottom25Sx}>
                          <Card sx={strategyMetricGrayBgSx}>
                            <CardContent>
                              <Typography>
                                Max Offer is the highest purchase
                                price that still meets all of your
                                strategy's targets. The Max Offer will
                                show a dash if the calculation is less
                                than zero or no purchase price can
                                meet all targets.
                              </Typography>
                              <Typography>
                                The Max Offer Price is calculated by
                                determining the the highest purchase
                                price that will meet each specific
                                target then taking the lowest value
                                from them. This means that any value
                                that affects a target besides purchase
                                price can affect the Max Offer Price
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <Divider />
                    <CardActions>
                      <Grid
                        container
                        sx={{
                          justifyContent: 'center',
                        }}
                      >
                        <Grid>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={toggleMaxOffer}
                          >
                            Close
                          </Button>
                        </Grid>
                      </Grid>
                    </CardActions>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Dialog>
          <Divider />
              </>
      )}
      <List component="div" sx={{
        paddingTop: 0,
        paddingBottom: 0,
        "& .MuiListItem-root, & .MuiListItemButton-root": {
          width: "100%",
          padding: "0.75rem 1.25rem",
          backgroundColor: "#fff",
          border: "1px solid rgba(0, 0, 0, 0.125)",
          borderRadius: 0,
          margin: 0,
          borderWidth: "0 0 1px",
          "&.MuiListItem-button": {
            borderRadius: 0,
            borderWidth: "0 0 1px",
            "& .MuiTouchRipple-root": { opacity: 0.2 },
          },
          "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
          "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
          "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
          "&:first-of-type": { borderTop: "0 !important" },
          "&:last-child": { borderBottom: "0 !important" },
        },
      }}>
        {strategySummary.analytics.map((section) => (
          <React.Fragment key={section.label}>
            <ListItemButton
              data-tour={section.tourStep}
              disableRipple
              onClick={() => toggle1(section.label)}
              sx={{
                transition: "all 0.2s ease-in-out !important",
                transform: "scale(1) !important",
                boxShadow: "none",
                position: "relative",
                zIndex: "0",
                "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                display: "flex",
                backgroundColor: "#fff",
                justifyContent: "space-between",
                py: "1rem",
              }}
            >
              <Box sx={[{
                height: "50px !important",
                width: "50px !important",
                lineHeight: "50px !important",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0,
                mr: "1rem",
              }, {
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mr: "0.25rem",
                position: "relative",
                fontSize: "1.2rem",
                transition: "all 0.2s ease-in-out",
                "&:last-child": { mr: 0, marginRight: 0 },
                "& .badge": { position: "absolute", right: 0, bottom: 0 },
                "& svg, & .svg-inline--fa": { display: "block" },
                marginRight: "0.25rem",
                textAlign: "center",
                textTransform: "uppercase",
              }]}>
                {!isUndefined(section.arrow) && (
                  <>
                    {section.arrow ? (
                      section.arrow === 2 ? (
                        <Avatar sx={{
                            backgroundColor: '#2296F3',
                          }}>
                          <ArrowUpwardIcon sx={strategyWhiteTextSx}/>
                        </Avatar>
                      ) : (
                        <Avatar sx={{
                            backgroundColor: '#46de64',
                          }}>
                          <ArrowUpwardIcon sx={strategyWhiteTextSx}/>
                        </Avatar>
                      )
                    ) : (
                      <Avatar sx={{
                          backgroundColor: '#F05248',
                        }}>
                        <ArrowDownwardIcon sx={strategyWhiteTextSx}/>
                      </Avatar>
                    )}
                  </>
                )}
              </Box>
              <Box sx={{ flex: '1 1 auto' }}>
                <Grid
                  container
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <Grid size="grow" sx={[{ fontWeight: 700 }, {
                    color: "#070919",
                    "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                  }]}>
                    {section.label}
                  </Grid>
                  <Grid size="auto" sx={{ ml: 'auto' }}>
                    <b>
                      {section.value !== '-'
                        ? section.type === 'currency'
                          ? currencyFormat.format(section.value)
                          : section.value > 10
                            ? '+1000%'
                            : percentageFormat.format(section.value)
                        : section.value}
                    </b>
                  </Grid>
                </Grid>
                <LinearProgress
                  variant="determinate"
                  sx={strategyMetricProgressSx(section.arrow !== 0)}
                  value={clampLinearProgressValue(
                    section.arrow
                      ? 100
                      : section.type === 'currency'
                        ? (section.value / section.target) * 100
                        : section.label === 'All-In Cost to ARV'
                          ? 200 -
                            (section.value / section.target) * 10000
                          : (section.value / section.target) * 10000,
                  )}
                />
                <Box sx={[{ display: 'flex', justifyContent: 'space-between', }, { fontSize: "0.8333333333rem !important" }]}>
                  {!isUndefined(section.arrow) && (
                    <Box
                      sx={
                        section.arrow === 0 ? { color: "#f83245" } : { color: "#1bc943" }
                      }
                    >
                      {section.arrow === 0
                        ? 'Below target'
                        : section.arrow === 1
                          ? 'Target met'
                          : 'Above target'}
                    </Box>
                  )}
                  <Box sx={{ opacity: "0.5 !important" }}>
                    Target:{' '}
                    {section.type === 'currency'
                      ? currencyFormat.format(section.target)
                      : section.target + '%'}
                  </Box>
                </Box>
              </Box>
            </ListItemButton>
            <Dialog
              open={modal1 === section.label}
              onClose={toggle1Close}
              maxWidth="lg"
              scroll="body"
              slotProps={{ paper: { sx: calcDialogPaperSx } }}
            >
              <Grid container spacing={0} direction="row">
                <Grid size={{ sm: 5 }}>
                  <Box sx={[{ height: '100%' }, {
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    "& > .divider": { zIndex: 7, width: "100%" },
                    "& .hero-footer": { zIndex: 7 },
                    "& .header-nav-wrapper": { zIndex: 27 },
                    "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                    borderTopLeftRadius: "inherit",
                    borderTopRightRadius: "inherit",
                    alignItems: "center",
                    ".MuiCard-root > &": {
                      top: -1,
                      marginLeft: -1,
                      marginRight: -1,
                      marginBottom: -2,
                      zIndex: 6,
                    },
                    backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
                  }, {
                    "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                  }]}>
                    <Box
                      sx={{
                        flexGrow: 1,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={[strategyNavyBgSx, { borderRadius: '0.65rem' }, {
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: "100%",
                        zIndex: 5,
                        opacity: "0.7 !important",
                      }, {
                        "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                      }]}/>
                      <Box sx={[{ justifyContent: 'center', textAlign: 'center', p: '3rem', '@media (min-width: 1440px)': { justifyContent: 'flex-start', textAlign: 'left' }, }, { position: "relative", zIndex: "7", width: "100%" }]}>
                        <Box sx={{ color: '#fff', mt: '1rem' }}>
                          <Typography sx={strategyCardTitleSx}>
                            Calculation
                          </Typography>
                        </Box>
                        {section.calculation}
                        <Box sx={{ color: '#fff', mt: '1rem' }}>
                          <Typography>
                            * Calculation may be a few dollars off due
                            to rounding
                          </Typography>
                          {section.label === 'Buyer Profit' && (
                            <Typography>
                              * Resell price includes the Assignment
                              Fee
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ sm: 7 }}>
                  <Box sx={[{ height: '100%' }, {
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    "& > .divider": { zIndex: 7, width: "100%" },
                    "& .hero-footer": { zIndex: 7 },
                    "& .header-nav-wrapper": { zIndex: 27 },
                    "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                  }, {
                    "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                  }]}>
                    <Card sx={{ flexGrow: 1, width: '100%' }}>
                      <CardHeader title={section.label} />
                      <Divider />
                      <CardContent>
                        <Grid
                          container
                          direction="row"
                          sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Grid
                            size={12} sx={strategyMarginBottom25Sx}>
                            <Card sx={strategyMetricGrayBgSx}>
                              <CardContent>
                                <Typography>
                                  {section.description}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          {section.extraContent}
                          {renderInputFields({
                            type: 'slider',
                            name: section.targetName,
                            label: section.targetLabel,
                            value:
                              assumptionsInput[section.targetName],
                            xs: 12,
                            assumptions: true,
                            valueLabelFormat: section.valueLabelFormat
                              ? section.valueLabelFormat
                              : (x) =>
                                  x === section.max
                                    ? `${x}+`
                                    : `${x}%`,
                            step: section.step,
                            min: section.min,
                            max: section.max,
                          })}
                        </Grid>
                      </CardContent>
                      <Divider />
                      <CardActions>
                        <Grid
                          container
                          sx={{
                            justifyContent: 'center',
                          }}
                        >
                          <Grid>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={toggle1Close}
                            >
                              Close
                            </Button>
                          </Grid>
                        </Grid>
                      </CardActions>
                    </Card>
                  </Box>
                </Grid>
              </Grid>
            </Dialog>
          </React.Fragment>
        ))}
      </List>
    </Card>
  )
}

/**
 * Comparable properties card component
 * @param {Object} props - Component props
 * @param {Array} props.compData - Comparable data
 * @param {Function} props.onComparablePropertiesTourClick - Function to handle comparable properties tour click
 * @param {Function} props.onToggleCenter - Function to handle center toggle
 * @param {Array} props.determinedComps - Determined comparables
 * @param {Array} props.compProperties - Comparable properties
 * @param {boolean} props.basicView - Whether this is a basic view
 * @returns {React.ReactElement} Comparable properties card
 */
export const ComparablePropertiesCard = ({
  compData,
  onComparablePropertiesTourClick,
  onToggleCenter,
  determinedComps,
  compProperties,
  basicView = false,
}) => {
  if (basicView) return null

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <Box sx={strategyMetricRowFlexSx}>
        <Box sx={{
          flexGrow: 1,
          minWidth: 0,
          "& > b": { display: "block" },
          "& > small": {
            textTransform: "uppercase",
            display: "block",
            opacity: 0.3,
            fontWeight: "bold",
          },
        }}>
          <b>Comparable Properties</b>
        </Box>
        <div>
          <Tooltip title="Details about comparable properties">
            <Button
              onClick={onComparablePropertiesTourClick}
             
              sx={{
                backgroundColor: "rgba(54, 140, 255, 0.15)",
                color: "#368cff",
                borderColor: "transparent",
                "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                height: "30px !important",
                width: "30px !important",
                lineHeight: "30px !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0,
                border: 0,
                textAlign: "left",
              }}
            >
              <FontAwesomeIcon icon={['fal', 'info-circle']} />
            </Button>
          </Tooltip>
        </div>
      </Box>
      <Divider />
      <CardContent>
        <Box sx={[{ borderRadius: '0.65rem', p: '0.5rem', mb: '0.5rem', backgroundColor: '#f8f9ff', textAlign: 'center', }, { borderWidth: "1px !important", borderStyle: "solid !important" }]} sx={strategyBorderMutedSx}>
          <Grid container sx={[strategyTextMuted802Sx, { fontSize: "0.875rem !important" }]}>
            <Grid
              size={{ md: 6 }}

              data-tour="comparableProperties-step-1"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <div>
                <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                  Comparable Properties
                </Box>
                <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                  <Box
                    sx={{
                      display: 'flex',
                      p: '0.25rem',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3,
                      }}
                    >
                      <CountUp
                        start={0}
                        end={determinedComps.length}
                        duration={1}
                        separator=","
                        decimals={0}
                      />
                    </Box>
                  </Box>
                </Box>
              </div>
            </Grid>
            <Grid
              size={{ md: 6 }}

              data-tour="comparableProperties-step-2"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <div>
                <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                  Potential Comps
                </Box>
                <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                  <Box
                    sx={{
                      display: 'flex',
                      p: '0.25rem',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3,
                      }}
                    >
                      <CountUp
                        start={0}
                        end={
                          compProperties.length -
                          determinedComps.length
                        }
                        duration={1}
                        separator=","
                        decimals={0}
                      />
                    </Box>
                  </Box>
                </Box>
              </div>
            </Grid>
          </Grid>
        </Box>
        <List component="div" sx={{
          paddingTop: 0,
          paddingBottom: 0,
          "& .MuiListItem-root, & .MuiListItemButton-root": {
            width: "100%",
            padding: "0.75rem 1.25rem",
            backgroundColor: "#fff",
            border: "1px solid rgba(0, 0, 0, 0.125)",
            borderRadius: 0,
            margin: 0,
            borderWidth: "0 0 1px",
            "&.MuiListItem-button": {
              borderRadius: 0,
              borderWidth: "0 0 1px",
              "& .MuiTouchRipple-root": { opacity: 0.2 },
            },
            "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
            "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
            "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
            "&:first-of-type": { borderTop: "0 !important" },
            "&:last-child": { borderBottom: "0 !important" },
          },
        }}>
          {map(compData, (section) => (
            <ListItemButton
              key={section.label}
              disableRipple
              data-tour={section.tourStep}
              sx={{
                transition: "all 0.2s ease-in-out !important",
                transform: "scale(1) !important",
                boxShadow: "none",
                position: "relative",
                zIndex: "0",
                "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                display: "flex",
                backgroundColor: "#fff",
                justifyContent: "space-between",
                py: "1rem",
              }}
            >
              <Grid
                container
                direction="row"
                sx={{
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  columnGap: 1,
                }}
              >
                <Grid size="grow" sx={[{ fontWeight: 700 }, {
                  color: "#070919",
                  "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                }]}>
                  {section.label}
                </Grid>
                {section.range ? (
                  <Grid size="auto">
                    <Grid
                      container
                      direction="row"
                      spacing={0.5}
                      sx={{
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                      }}
                    >
                      <Grid size="auto">
                        <CountUp
                          start={0}
                          end={section.value1}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={1}
                          decimal="."
                          prefix="$"
                          suffix="k"
                        />
                      </Grid>
                      <Grid size="auto">
                        <Typography>-</Typography>
                      </Grid>
                      <Grid size="auto">
                        <CountUp
                          start={0}
                          end={section.value2}
                          duration={1}
                          separator=","
                          delay={1}
                          decimals={1}
                          decimal="."
                          prefix="$"
                          suffix="k"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid size="auto">
                    <CountUp
                      start={0}
                      end={section.value}
                      duration={1}
                      separator=","
                      delay={1}
                      decimals={2}
                      decimal="."
                      prefix="$"
                    />
                  </Grid>
                )}
              </Grid>
            </ListItemButton>
          ))}
        </List>
        <Grid container>
          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
             
              data-tour="comparableProperties-step-7"
              onClick={(e) => onToggleCenter('Comps')} sx={strategyMarginTop25Sx}>
              <Typography>View Comps</Typography>
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

/**
 * Deactive card component
 * @returns {React.ReactElement} Deactive card
 */
export const DeactiveCard = () => {
  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <Box sx={strategyMetricRowFlexSx}>
        <Box sx={{
          flexGrow: 1,
          minWidth: 0,
          "& > b": { display: "block" },
          "& > small": {
            textTransform: "uppercase",
            display: "block",
            opacity: 0.3,
            fontWeight: "bold",
          },
        }}>
          <b>Hide Cards</b>
        </Box>
      </Box>
      <Divider />
      <CardContent>
        <Typography>
          All cards past this one will be hidden
        </Typography>
      </CardContent>
    </Card>
  )
}
